import { useState } from 'react';

import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { z } from 'zod';

import {
    PermissionGate,
    practiceMemberOptions,
    useUpdatePracticeMemberOptions,
} from '@/entities/practice-member';
import { PageTitle } from '@/shared/ui/text/page-title';
import { SettingsColumn } from '@/widgets/settings/settings-column';
import { SettingsEditableRow } from '@/widgets/settings/settings-editable-row';
import { SettingsSection } from '@/widgets/settings/settings-section';

import { PROFILE_CREDENTIALS_REQUIRED_ACCESS_SOURCES } from '../config/profile.config';
import { useEmailVerification } from '../model/use-email-verification';
import { ProfileCheckInboxDialog } from './profile-page/profile-check-inbox-dialog';
import { ProfileEmailRow } from './profile-page/profile-email-row';
import { ProfileLicensesSection } from './profile-page/profile-licenses-section';
import { ProfileNpiRow } from './profile-page/profile-npi-row';
import { ProfileVerificationExpiredDialog } from './profile-page/profile-verification-expired-dialog';

const route = getRouteApi('/settings/profile');

const optionalFreeTextSchema = z.string();
const preferredNameSchema = z.string().min(1, 'Enter preferred name');

export function ProfilePage() {
    const { data: practiceMember } = useSuspenseQuery(practiceMemberOptions());
    const [editingField, setEditingField] = useState<string | null>(null);
    const updateMutation = useMutation(useUpdatePracticeMemberOptions());

    const { emailVerification } = route.useSearch();
    const navigate = route.useNavigate();

    const clearEmailVerificationParam = () => {
        void navigate({ search: {}, replace: true });
    };

    const {
        email: accountEmail,
        isVerified,
        isResending,
        openDialog,
        closeDialog,
        resendFromSettings,
        requestNewLink,
    } = useEmailVerification(emailVerification, clearEmailVerificationParam);

    const handleSaveLegalName = (legalName: string | null = null) => {
        updateMutation.mutate({ legalName });
    };

    const handleSavePreferredName = (preferredFirstName: string) => {
        updateMutation.mutate({ preferredFirstName });
    };

    const handleSaveTitle = (title: string | null = null) => {
        updateMutation.mutate({ title });
    };

    const handleSaveNpi = (npi: string | null) => {
        updateMutation.mutate({ npi });
    };

    return (
        <SettingsColumn className="space-y-8">
            <PageTitle>Profile</PageTitle>

            <SettingsSection title="Contact information" className="mb-12">
                <SettingsEditableRow
                    label="Legal name"
                    value={practiceMember.legalName}
                    description="Use the name that appears on your licensures"
                    inputLabel="Legal name (optional)"
                    schema={optionalFreeTextSchema}
                    onSave={handleSaveLegalName}
                    isDisabled={editingField !== null && editingField !== 'legalName'}
                    onEditingChange={(isEditing) => setEditingField(isEditing ? 'legalName' : null)}
                    isOptional
                />
                <SettingsEditableRow
                    label="Preferred name"
                    value={practiceMember.preferredFirstName}
                    description="This is how we will address you"
                    schema={preferredNameSchema}
                    onSave={handleSavePreferredName}
                    isDisabled={editingField !== null && editingField !== 'preferredFirstName'}
                    onEditingChange={(isEditing) =>
                        setEditingField(isEditing ? 'preferredFirstName' : null)
                    }
                />
                <ProfileEmailRow
                    email={accountEmail ?? practiceMember.email}
                    isVerified={isVerified}
                    isResending={isResending}
                    onResend={resendFromSettings}
                    isDisabled={editingField !== null && editingField !== 'email'}
                    onEditingChange={(isEditing) => setEditingField(isEditing ? 'email' : null)}
                />
            </SettingsSection>

            <PermissionGate requiredAccessSources={PROFILE_CREDENTIALS_REQUIRED_ACCESS_SOURCES}>
                <ProfileLicensesSection
                    isDisabled={editingField !== null && editingField !== 'licenses'}
                    onEditingChange={(isEditing) => setEditingField(isEditing ? 'licenses' : null)}
                />
            </PermissionGate>

            <SettingsSection title="Clinical details">
                <SettingsEditableRow
                    label="Title"
                    value={practiceMember.title}
                    inputLabel="Title (optional)"
                    schema={optionalFreeTextSchema}
                    onSave={handleSaveTitle}
                    isDisabled={editingField !== null && editingField !== 'title'}
                    onEditingChange={(isEditing) => setEditingField(isEditing ? 'title' : null)}
                    isOptional
                />
                <PermissionGate requiredAccessSources={PROFILE_CREDENTIALS_REQUIRED_ACCESS_SOURCES}>
                    <ProfileNpiRow
                        value={practiceMember.npi}
                        onSave={handleSaveNpi}
                        isDisabled={editingField !== null && editingField !== 'npi'}
                        onEditingChange={(isEditing) => setEditingField(isEditing ? 'npi' : null)}
                    />
                </PermissionGate>
            </SettingsSection>

            <ProfileCheckInboxDialog open={openDialog === 'check-inbox'} onClose={closeDialog} />
            <ProfileVerificationExpiredDialog
                open={openDialog === 'expired'}
                isPending={isResending}
                onRequestLink={requestNewLink}
                onClose={closeDialog}
            />
        </SettingsColumn>
    );
}
